import { IAsset } from 'src/app/models/products/asset';
import { ITouch } from 'src/app/models/products/touch';

export type StepType = 'preProduction' | 'production' | 'postProduction' | 'outputAssets';

export interface ParseAssetContext {
  readonly step: StepType;
  readonly listProduction: any[];
  readonly hasPreReleasePermission: boolean;
}

export interface SelectionState {
  allSelected: boolean;
  selectedCount: number;
  displaySelectTouch: { [key: string]: boolean };
}

export function parseAsset(assets: IAsset[], context: ParseAssetContext): any[] {
  let angleIndex = 1;
  let videoIndex = 1;
  return assets.map((asset: IAsset) => {
    let label = `${asset.type === 'Still' ? 'Angle' : 'Video'} `;
    let subLabel = label;
    if (context.step === 'production') {
      if (asset.additionalAssetIndex != null) {
        label += `${(asset.type === 'Still' ? angleIndex : videoIndex) - 1}.${
          asset.additionalAssetIndex + 1
        }`;
      } else if (asset.type === 'Still') {
        label += asset.packageAssetIndex / 1000 + 1;
        angleIndex++;
      } else {
        const touch = context.listProduction.find(
          (t: ITouch) => t.id === asset.masterSuiteTouchId,
        );
        const imageCount = touch.assets.filter(
          (a: IAsset) => a.type === 'Still' && a.childIndex == null,
        ).length;
        label += asset.packageAssetIndex / 1000 + 1 - imageCount;
        videoIndex++;
      }
    }

    if (context.step === 'postProduction' || context.step === 'outputAssets') {
      label = asset.selectsIndex >= 0 ? `Select ${asset.selectsIndex / 1000 + 1}` : 'Non-Select';
      if (asset.type === 'Still') {
        subLabel = `Touch ${
          asset.masterSuiteTouchIndex / 1000 + 1
        } / ${subLabel} ${asset.packageAssetIndex / 1000 + 1}`;
      } else {
        subLabel = `Touch ${
          asset.masterSuiteTouchIndex / 1000 + 1
        } / ${subLabel} ${asset.videoIndex}`;
      }

      if (asset.additionalAssetIndex != null) {
        subLabel += `.${asset.additionalAssetIndex + 1}`;
      }
    }
    if (context.step === 'outputAssets' && asset.id) {
      label = `Select ${asset.selectsIndex / 1000 + 1}`;
      const parts = asset.reviewUri.split('/');
      subLabel = parts[parts.length - 1];
      subLabel = subLabel.split('?')[0];
    }

    const status = context.hasPreReleasePermission ? asset.internalReviewState : asset.clientReviewState;

    return {
      ...asset,
      label,
      subLabel,
      status,
    };
  });
}

// Regroups the legacy backend shape (one row per OutputFormat, each with its
// outputAssets[]) into one row per OutputFormula. All assets that reference
// the same outputFormulaId land in the same section — stills and videos
// mixed. Formula id/name/kind are lifted from the first asset in the group
// (all assets in a group share the same formula, so first-wins is safe).
//
// Legacy fallback: assets missing outputFormulaId keep their source
// OutputFormat as the section (id + name from the legacy format), so
// pre-backfill rows still render coherently until they're migrated.
export function regroupOutputAssetsByFormula(formats: any[]): any[] {
  const groups = new Map<
    string,
    { id: string; name: string; outputFormulaKind: string | null; outputAssets: any[] }
  >();

  formats.forEach((format: any) => {
    const legacyKey = format.id;
    const legacyName = format.name;
    (format.outputAssets ?? []).forEach((asset: any) => {
      const key = asset.outputFormulaId ?? legacyKey;
      const name = asset.outputFormulaName ?? legacyName;
      const kind = asset.outputFormulaKind ?? null;
      if (!groups.has(key)) {
        groups.set(key, { id: key, name, outputFormulaKind: kind, outputAssets: [] });
      }
      groups.get(key)!.outputAssets.push(asset);
    });
  });

  return Array.from(groups.values());
}

export function parseStepContentData(
  data: any[],
  context: ParseAssetContext,
  isOutputAssets?: boolean,
): any[] {
  if (isOutputAssets) {
    return data.map((format: any) => {
      const assets = parseAsset(format.outputAssets, context);
      return {
        ...format,
        assets,
      };
    });
  }
  return data.map((touch: any) => {
    const assets = parseAsset(touch.assets, context);
    return {
      ...touch,
      assets,
    };
  });
}

export function setAllSelected(content: any[], completed: boolean): { count: number } {
  let count = 0;
  content.forEach((t: any) => {
    t.quantitySelected = 0;
    t.selectedTouch = completed;
    t.assets.forEach((asset: any) => {
      if (asset.thumbnailUri && !asset.childIndex && asset.id) {
        asset.selected = completed;
        if (completed) {
          count++;
          t.quantitySelected++;
        }
      }
    });
  });
  return { count };
}

export function selectAllInTouch(content: any[], touchId: string, completed: boolean): void {
  content.forEach((t: any) => {
    if (t.id === touchId) {
      t.selectedTouch = completed;
      t.assets.forEach((asset: any) => {
        if (asset.thumbnailUri && !asset.childIndex && asset.id) {
          asset.selected = completed;
        }
      });
    }
  });
}

export function onCardSelected(content: any[], touchId: string): void {
  content.forEach((touch: any) => {
    if (touch.id === touchId) {
      const allSelected = touch.assets
        .filter((asset: any) => !asset.childIndex && asset.id)
        .every((asset: any) => asset.selected);
      touch.selectedTouch = allSelected;
    }
  });
}

export function checkAllSelected(content: any[]): boolean {
  return content.every((touch: any) => touch.selectedTouch);
}

export function countSelectedAssets(content: any[]): number {
  let count = 0;
  content.forEach((touch: any) => {
    touch.quantitySelected = 0;
    touch.assets.forEach((asset: any) => {
      if (asset.selected && !asset.childIndex && asset.id) {
        count++;
        touch.quantitySelected++;
      }
    });
  });
  return count;
}

export function isDownloadDisabled(content: any[]): boolean {
  return !content.some((touch: any) =>
    touch.assets.some((asset: any) => asset.selected),
  );
}

export function hasSelectableAssets(content: any[], touchId: string): boolean {
  return content
    .filter((touch: any) => touch.id === touchId)
    .some((touch: any) => touch.assets.some((asset: any) => asset.id));
}
